import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Stack,
  Avatar,
  Dialog,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Tooltip,
  
} from '@mui/material'
import {
  Add as AddIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  ViewColumn as ViewColumnIcon,
  Undo as UndoIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import AdditionalOptions from './AdditionalOptions'
import ItemsTableDesktop from './ItemsTableDesktop'
import ItemsListMobile from './ItemsListMobile'
import PaymentMethods from './PaymentMethods'
import type { InvoiceFormData } from './types'
import ColumnConfigPopover from './ColumnConfigPopover'
import AttachmentsPanel from './AttachmentsPanel'
import BasicInfoCard from './BasicInfoCard'
import CustomerInfoCard from './CustomerInfoCard'
import { useLineItemFields } from './useLineItemFields'
import { columnDefinitions } from './columns'
import { useInvoiceInitialData } from './useInvoiceInitialData'
import { useInvoiceForm } from './useInvoiceForm'
import { useColumnConfig } from './useColumnConfig'
import { useInvoiceNavigation } from './useInvoiceNavigation'
import FloatingNavigationBar from './FloatingNavigationBar'
import { transformApiInvoiceToFormData } from './transformInvoiceData'

// Enhanced interfaces moved to types.ts

interface InvoiceFormProps {
  invoice?: any
  onSave?: (data: InvoiceFormData) => void
  onCancel?: () => void
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoice,
  onSave,
  onCancel
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  
  const {
    formData,
    setFormData,
    updateLineItem,
    addLineItem,
    removeLineItem,
    updatePaymentMethod,
    addPaymentMethod,
    removePaymentMethod,
    applyDefaults,
    resetForm,
    grandTotal,
    totalPayments
  } = useInvoiceForm()

  const {
    isAuthenticated,
    loadingInit,
    branches,
    staff,
    salesSettings,
    accountGuideMap,
    productNames,
    currencies,
    paymentMethods,
    defaults
  } = useInvoiceInitialData()

  // Apply API-provided defaults when available
  useEffect(() => {
    if (defaults) {
      applyDefaults(defaults as Partial<InvoiceFormData>)
    }
  }, [defaults, applyDefaults])

  const accountOptions = useMemo(() => {
    return Object.values(accountGuideMap).map((g: any, index: number) => ({
      value: g.total_code || g.code, // Use account code instead of ID
      id: Number(g.id), // Keep ID for reference
      label: `${g.total_code || g.code} - ${(g.name_a_original || g.name_a || g.name_e || '').toString()}`,
      name: (g.name_a_original || g.name_a || g.name_e || '').toString(),
      uniqueKey: `account-${g.id}` // Use account ID for unique keys
    }))
  }, [accountGuideMap])

  const productOptions = useMemo(() => {
    const options = productNames.map((p: any, index: number) => ({
      value: p.total_code, // Use total_code as the product code (e.g., "101")
      id: Number(p.id), // Keep ID for reference
      label: `${p.total_code} - ${(p.name_a_original || p.name_a || p.name_e || '').toString()}`,
      name: (p.name_a_original || p.name_a || p.name_e || '').toString(),
      uniqueKey: `product-${p.id}` // Use product ID for unique keys
    }))
    return options
  }, [productNames])

  // Human-readable currency label for totals and summaries
  const currencyLabel = useMemo(() => {
    const curr = Array.isArray(currencies)
      ? currencies.find((c: any) => Number(c?.id) === Number(formData.currency))
      : null
    return (
      curr?.name_a_original ||
      curr?.name_a ||
      curr?.code ||
      (formData.currency ? String(formData.currency) : '')
    ) as string
  }, [currencies, formData.currency])

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [showImagePreview, setShowImagePreview] = useState(false)
  const [columnConfigAnchor, setColumnConfigAnchor] = useState<HTMLElement | null>(null)
  const [isEditable, setIsEditable] = useState(false)
  const [isNewInvoice, setIsNewInvoice] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [originalFormData, setOriginalFormData] = useState<InvoiceFormData | null>(null)
  
  // Column configuration state via hook
  const { columnConfig, handleChange: handleColumnConfigChange, visibleColumns, showAll, showEssentials } = useColumnConfig()

  // Invoice navigation hook
  const {
    currentInvoice,
    isLoading: isLoadingNavigation,
    navigateToInvoice,
    loadFirstInvoice
  } = useInvoiceNavigation({
    branchId: defaults?.def_branch || 1,
    onInvoiceChange: (invoice) => {
      console.log('=== ON INVOICE CHANGE CALLBACK ===')
      console.log('invoice received:', invoice)
      console.log('isEditable:', isEditable)
      console.log('isNewInvoice:', isNewInvoice)
      
      if (invoice && !isEditable && !isNewInvoice) {
        console.log('✅ Conditions met, transforming and setting form data')
        // Transform the API response to form data structure
        const transformedData = transformApiInvoiceToFormData(
          invoice,
          branches,
          accountGuideMap as any,
          paymentMethods,
          defaults,
          salesSettings
        )
        console.log('Loading invoice:', invoice)
        console.log('Transformed data:', transformedData)
        // Populate the form with the transformed data
        setFormData(transformedData)
        console.log('✅ setFormData called with transformed data')
        
        // Form data has been successfully populated
      } else {
        console.log('❌ Conditions NOT met for setting form data')
      }
    }
  })

  // Stable branchId for navigation bar and API calls
  const selectedBranchId = useMemo(() => {
    const ls = localStorage.getItem('selectedBranch')
    const lsId = ls ? parseInt(ls, 10) : null
    return defaults?.def_branch || lsId || 1
  }, [defaults])

  // Load first invoice when component mounts (if not in edit mode)
  useEffect(() => {
    console.log('=== INVOICE LOADING USEEFFECT ===')
    console.log('defaults:', defaults)
    console.log('isEditable:', isEditable)
    console.log('isNewInvoice:', isNewInvoice)
    console.log('currentInvoice:', currentInvoice)
    console.log('isAuthenticated:', isAuthenticated)
    
    if (defaults && !isEditable && !isNewInvoice && !currentInvoice) {
      console.log('✅ Conditions met, calling loadFirstInvoice()')
      loadFirstInvoice()
    } else {
      console.log('❌ Conditions NOT met for loading first invoice')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults, isEditable, isNewInvoice]) // Intentionally excluding currentInvoice and loadFirstInvoice to prevent infinite loops

  // Track changes to form data
  useEffect(() => {
    if (originalFormData && (isEditable || isNewInvoice)) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData)
      setHasUnsavedChanges(hasChanges)
    }
  }, [formData, originalFormData, isEditable, isNewInvoice])

  // Use extracted field renderers (SRP)
  const { renderTableCell: renderTableCellFromHook, renderMobileField: renderMobileFieldFromHook } = useLineItemFields({
    productOptions,
    updateLineItem,
    removeLineItem,
    formData,
    disabled: !isEditable,
    currencyLabel
  })

  

  const handleSubmit = () => {
    if (onSave) {
      onSave(formData)
      setHasUnsavedChanges(false)
      setIsEditable(false)
      setIsNewInvoice(false)
    }
  }

  const handleNewInvoice = () => {
    // Save current state as original for undo
    setOriginalFormData({ ...formData })
    // Reset form for new invoice
    resetForm()
    setIsEditable(true)
    setIsNewInvoice(true)
    setHasUnsavedChanges(false)
  }

  const handleEditInvoice = () => {
    // Save current state as original for undo
    setOriginalFormData({ ...formData })
    setIsEditable(true)
    setIsNewInvoice(false)
    setHasUnsavedChanges(false)
  }

  const handleUndoChanges = () => {
    if (originalFormData) {
      // Restore original state
      setFormData(originalFormData)
      setIsEditable(false)
      setIsNewInvoice(false)
      setHasUnsavedChanges(false)
      setOriginalFormData(null)
    }
  }

  // Handle invoice navigation
  const handleInvoiceNavigation = async (bondNum: string) => {
    if (isEditable || isNewInvoice) {
      // Warn user about unsaved changes
      const confirmNavigation = window.confirm('لديك تغييرات غير محفوظة. هل تريد المتابعة بدون حفظ؟')
      if (!confirmNavigation) {
        return
      }
    }
    
    try {
      await navigateToInvoice(bondNum)
    } catch (error) {
      console.error('Navigation failed:', error)
    }
  }



  // Show loading state while waiting for authentication or initial data (keep mounted during navigation loading)
  if (!isAuthenticated || loadingInit) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">
            {!isAuthenticated ? 'جاري التحقق من المصادقة...' : 'جاري تحميل البيانات الأساسية...'}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      bgcolor: '#f5f5f5',
      p: { xs: 0.5, sm: 1 },
      pb: { xs: 2, sm: 2 } // minimal spacing on outer gray area
    }}>
      {/* Non-destructive overlay during invoice navigation loading */}
      {isLoadingNavigation && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(255,255,255,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 1 }} />
            <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
              جاري تحميل الفاتورة...
            </Typography>
          </Box>
        </Box>
      )}
      {/* Header */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 1.25, sm: 3 }, 
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2
        }}
      >
        <Stack 
          direction={{ xs: 'row', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems="center"
          spacing={{ xs: 1, sm: 0 }}
          sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: { xs: 1, sm: 0 } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                width: { xs: 36, sm: 56 }, 
                height: { xs: 36, sm: 56 },
                display: { xs: 'none', sm: 'flex' },
                border: '2px solid rgba(255,255,255,0.3)'
              }}
            >
              <ReceiptIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography 
                variant="h4" 
                fontWeight="700"
                sx={{ 
                  fontSize: { xs: '1rem', sm: '2rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  mb: 0.5
                }}
              >
                {invoice ? 'تعديل الفاتورة' : 'فاتورة مبيعات جديدة'}
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  opacity: 0.9,
                  fontWeight: 400,
                  fontSize: { xs: '0.75rem', sm: '1rem' }
                }}
              >
                رقم الفاتورة: {formData.invoice_number}
              </Typography>
            </Box>
          </Box>
          
          <Stack 
            direction={{ xs: 'row', sm: 'row' }} 
            spacing={0.5}
            sx={{ width: { xs: 'auto', sm: 'auto' }, flexWrap: 'wrap' }}
          >
            {!isEditable && (
              isMobile ? (
                <Tooltip title="جديد">
                  <IconButton
                    onClick={handleNewInvoice}
                    size="small"
                    sx={{
                      color: 'white',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      p: 0,
                      flexShrink: 0,
                      border: '1px solid rgba(255,255,255,0.35)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' }
                    }}
                    aria-label="جديد"
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleNewInvoice}
                  sx={{ 
                    minWidth: { xs: 'auto', sm: 100 },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: { xs: 0.4, sm: 1 },
                    px: { xs: 1, sm: 2.5 },
                    borderRadius: 3,
                    fontSize: { xs: '0.75rem', sm: '0.9rem' },
                    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                    boxShadow: '0 4px 14px rgba(33, 150, 243, 0.3)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
                      boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>جديد</Box>
                </Button>
              )
            )}
            {!isNewInvoice && (
              isMobile ? (
                <Tooltip title="تعديل">
                  <IconButton
                    onClick={handleEditInvoice}
                    size="small"
                    sx={{
                      color: 'white',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      p: 0,
                      flexShrink: 0,
                      border: '1px solid rgba(255,255,255,0.35)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' }
                    }}
                    aria-label="تعديل"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={handleEditInvoice}
                  sx={{ 
                    minWidth: { xs: 'auto', sm: 100 },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: { xs: 0.4, sm: 1 },
                    px: { xs: 1, sm: 2.5 },
                    borderRadius: 3,
                    fontSize: { xs: '0.75rem', sm: '0.9rem' },
                    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                    boxShadow: '0 4px 14px rgba(255, 152, 0, 0.3)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
                      boxShadow: '0 6px 20px rgba(255, 152, 0, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>تعديل</Box>
                </Button>
              )
            )}
            {!isNewInvoice && !isEditable && (
              isMobile ? (
                <Tooltip title="حذف">
                  <IconButton
                    size="small"
                    sx={{
                      color: 'white',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      p: 0,
                      flexShrink: 0,
                      border: '1px solid rgba(255,255,255,0.35)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' }
                    }}
                    aria-label="حذف"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  sx={{ 
                    minWidth: { xs: 'auto', sm: 90 },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: { xs: 0.4, sm: 1 },
                    px: { xs: 1, sm: 2.5 },
                    borderRadius: 3,
                    fontSize: { xs: '0.75rem', sm: '0.9rem' },
                    background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
                    boxShadow: '0 4px 14px rgba(244, 67, 54, 0.3)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #D32F2F 0%, #C62828 100%)',
                      boxShadow: '0 6px 20px rgba(244, 67, 54, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>حذف</Box>
                </Button>
              )
            )}
            {!isNewInvoice && !isEditable && (
              isMobile ? (
                <Tooltip title="طباعة">
                  <IconButton
                    size="small"
                    sx={{
                      color: 'white',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      p: 0,
                      flexShrink: 0,
                      border: '1px solid rgba(255,255,255,0.35)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' }
                    }}
                    aria-label="طباعة"
                  >
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="inherit"
                  startIcon={<PrintIcon />}
                  sx={{ 
                    minWidth: { xs: 'auto', sm: 100 },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: { xs: 0.4, sm: 1 },
                    px: { xs: 1, sm: 2.5 },
                    borderRadius: 3,
                    fontSize: { xs: '0.75rem', sm: '0.9rem' },
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                      transform: 'translateY(-2px)',
                      border: '1px solid rgba(255,255,255,0.4)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>طباعة</Box>
                </Button>
              )
            )}
            {(isEditable || isNewInvoice) && (
              isMobile ? (
                <Tooltip title="حفظ">
                  <IconButton
                    onClick={handleSubmit}
                    size="small"
                    sx={{
                      color: 'white',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      p: 0,
                      flexShrink: 0,
                      border: '1px solid rgba(255,255,255,0.35)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' }
                    }}
                    aria-label="حفظ"
                    disabled={!isEditable}
                  >
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 120 },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1,
                    px: 3,
                    borderRadius: 3,
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                    boxShadow: '0 4px 14px rgba(76, 175, 80, 0.3)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
                      boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 100%)',
                      color: '#9E9E9E',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transform: 'none'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                  disabled={!isEditable}
                >
                  {hasUnsavedChanges ? 'حفظ التغييرات' : 'حفظ'}
                </Button>
              )
            )}
            {(isEditable || isNewInvoice) && (
              isMobile ? (
                <Tooltip title="تراجع">
                  <IconButton
                    onClick={() => {
                      if (hasUnsavedChanges) {
                        if (window.confirm('هل تريد التراجع؟ سيتم فقدان التغييرات غير المحفوظة.')) {
                          handleUndoChanges()
                        }
                      } else {
                        handleUndoChanges()
                      }
                    }}
                    size="small"
                    sx={{ 
                      color: 'white',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      p: 0,
                      flexShrink: 0,
                      border: '1px solid rgba(255,255,255,0.35)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' }
                    }}
                    aria-label="تراجع"
                  >
                    <UndoIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="inherit"
                  startIcon={<UndoIcon />}
                  onClick={() => {
                    if (hasUnsavedChanges) {
                      if (window.confirm('هل تريد التراجع؟ سيتم فقدان التغييرات غير المحفوظة.')) {
                        handleUndoChanges()
                      }
                    } else {
                      handleUndoChanges()
                    }
                  }}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 100 },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1,
                    px: 2.5,
                    borderRadius: 3,
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                      transform: 'translateY(-2px)',
                      border: '1px solid rgba(255,255,255,0.4)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  تراجع
                </Button>
              )
            )}
            {isMobile ? (
              <Tooltip title="إغلاق">
                <IconButton
                  aria-label="إغلاق"
                  onClick={() => {
                    if (hasUnsavedChanges) {
                      if (window.confirm('هل تريد إغلاق الفاتورة؟ سيتم فقدان التغييرات غير المحفوظة.')) {
                        navigate(-1)
                      }
                    } else {
                      navigate(-1)
                    }
                  }}
                  size="small"
                  sx={{
                    color: 'white',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    width: 36,
                    height: 36,
                    p: 0,
                    flexShrink: 0,
                    border: '1px solid rgba(255,255,255,0.35)',
                    '&:hover': { background: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton
                onClick={() => {
                  if (hasUnsavedChanges) {
                    if (window.confirm('هل تريد إغلاق الفاتورة؟ سيتم فقدان التغييرات غير المحفوظة.')) {
                      navigate(-1)
                    }
                  } else {
                    navigate(-1)
                  }
                }}
                color="inherit"
                sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 1 
      }}>
        {/* Left Panel - Form */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          minHeight: { xs: 'auto', lg: '100%' },
          pb: { xs: 0, sm: 12 }
        }}>
          <Stack spacing={1}>
            {/* Basic Information */}
            <BasicInfoCard
              formData={formData}
              setFormData={setFormData}
              branches={branches}
              staff={staff}
              salesSettings={salesSettings}
              disabled={!isEditable}
            />

            {/* Customer Information */}
            <CustomerInfoCard
              formData={formData}
              setFormData={setFormData}
              accountOptions={accountOptions as any}
              disabled={!isEditable}
            />

            {/* Items Header Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ViewColumnIcon />}
                    onClick={(e: React.MouseEvent<HTMLElement>) => setColumnConfigAnchor(e.currentTarget)}
                    sx={{ 
                      borderRadius: 3,
                      fontWeight: 500,
                      py: 1,
                      px: 2,
                      fontSize: '0.85rem',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      background: 'rgba(33, 150, 243, 0.04)',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        background: 'rgba(33, 150, 243, 0.08)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.2)'
                      },
                      '&:active': {
                        transform: 'translateY(0px)'
                      },
                      '&:disabled': {
                        borderColor: 'grey.300',
                        color: 'grey.400',
                        background: 'grey.50'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    disabled={!isEditable}
                  >
                    إعدادات الأعمدة
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addLineItem}
                  sx={{
                    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                    color: 'white',
                    fontWeight: 600,
                    px: 3.5,
                    py: 1.5,
                    borderRadius: 3,
                    fontSize: '0.9rem',
                    boxShadow: '0 4px 16px rgba(255, 152, 0, 0.3)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(255, 152, 0, 0.4)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 100%)',
                      color: '#9E9E9E',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transform: 'none'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                  disabled={!isEditable}
                >
                  إضافة صنف جديد
                </Button>
              </Box>
            </Box>

            {/* Items Desktop */}
            {!isMobile && (
              <ItemsTableDesktop
                columns={visibleColumns}
                items={formData.line_items}
                renderCell={(item, index, key) => renderTableCellFromHook(item, index, key)}
              />
            )}

            {/* Items Mobile */}
            {isMobile && (
              <ItemsListMobile
                items={formData.line_items}
                visibleColumns={visibleColumns}
                renderField={(item, index, key) => renderMobileFieldFromHook(item, index, key)}
                columnConfig={columnConfig}
                onOpenColumnConfig={(e) => setColumnConfigAnchor(e.currentTarget)}
                disabled={!isEditable}
              />
            )}

            {/* Items Totals */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  عدد الأصناف: {formData.line_items.length}
                </Typography>
                <Typography variant="h6" color="primary">
                  الإجمالي: {grandTotal.toFixed(2)} {currencyLabel}
                </Typography>
              </Box>
            </Box>

            {/* Payment Methods */}
            <PaymentMethods
              payments={formData.payment_methods}
              isMobile={isMobile}
              onUpdate={(index, field, value) => updatePaymentMethod(index, field as any, value)}
              onRemove={(index) => removePaymentMethod(index)}
              onAdd={addPaymentMethod}
              canRemove={(index) => formData.payment_methods.length > 1}
              total={totalPayments}
              currency={currencyLabel}
              currencies={currencies}
              paymentMethods={paymentMethods}
              disabled={!isEditable}
            />

            {/* Additional Options */}
            <AdditionalOptions
              notes={formData.notes}
              onNotesChange={(value) => setFormData(prev => ({ ...prev, notes: value }))}
              zatcaReported={formData.zatca_reported}
              onZatcaReportedChange={(checked) => setFormData(prev => ({ ...prev, zatca_reported: checked }))}
              onQrClick={() => {}}
              disabled={!isEditable}
            />

            {/* Modern Save Button Bar - Only show when editable */}
            {(isEditable || isNewInvoice) && (
              <Box sx={{ 
                mt: 3, 
                p: 3,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                borderRadius: 3,
                boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                  animation: 'shimmer 2s infinite'
                },
                '@keyframes shimmer': {
                  '0%': { left: '-100%' },
                  '100%': { left: '100%' }
                }
              }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
              }}>
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography variant="h6" sx={{ 
                    color: 'white',
                    fontWeight: 700,
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    mb: 0.5
                  }}>
                    جاهز للحفظ؟
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '0.9rem'
                  }}>
                    تأكد من صحة البيانات قبل حفظ الفاتورة
                  </Typography>
                </Box>
                
                {(isEditable || isNewInvoice) && (
                  isMobile ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="حفظ">
                        <IconButton
                          onClick={handleSubmit}
                          size="small"
                          sx={{
                            color: 'white',
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            p: 0,
                            flexShrink: 0,
                            border: '1px solid rgba(255,255,255,0.35)',
                            '&:hover': { background: 'rgba(255,255,255,0.3)' }
                          }}
                          disabled={!isEditable}
                          aria-label="حفظ"
                        >
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="تراجع">
                        <IconButton
                          onClick={() => {
                            if (hasUnsavedChanges) {
                              if (window.confirm('هل تريد التراجع؟ سيتم فقدان التغييرات غير المحفوظة.')) {
                                handleUndoChanges()
                              }
                            } else {
                              handleUndoChanges()
                            }
                          }}
                          size="small"
                          sx={{
                            color: 'white',
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            p: 0,
                            flexShrink: 0,
                            border: '1px solid rgba(255,255,255,0.35)',
                            '&:hover': { background: 'rgba(255,255,255,0.3)' }
                          }}
                          aria-label="تراجع"
                        >
                          <UndoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSubmit}
                      sx={{ 
                        minWidth: { xs: '100%', sm: 200 },
                        background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                        color: 'white',
                        fontWeight: 700,
                        py: 2.5,
                        px: 5,
                        borderRadius: 4,
                        fontSize: '1.1rem',
                        textTransform: 'none',
                        boxShadow: '0 8px 32px rgba(76, 175, 80, 0.4)',
                        border: '2px solid rgba(255,255,255,0.2)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                          transition: 'left 0.6s ease'
                        },
                        '&:hover': {
                          background: 'linear-gradient(135deg, #388E3C 0%, #1B5E20 100%)',
                          transform: 'translateY(-4px) scale(1.02)',
                          boxShadow: '0 12px 40px rgba(76, 175, 80, 0.5)',
                          '&::before': {
                            left: '100%'
                          }
                        },
                        '&:active': {
                          transform: 'translateY(-2px) scale(1.01)',
                          boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                          background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)'
                        },
                        '&:disabled': {
                          background: 'linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 100%)',
                          color: '#9E9E9E',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                          transform: 'none',
                          '&::before': {
                            display: 'none'
                          }
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                      disabled={!isEditable}
                    >
                      حفظ الفاتورة
                    </Button>
                  )
                )}
              </Box>
            </Box>
            )}
          </Stack>
        </Box>

        {/* Right Panel - Image Upload & Preview - Hidden on mobile */}
        {!isMobile && (
          <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <AttachmentsPanel
              variant="sidebar"
              selectedFile={selectedFile}
              onFileChange={(file) => setSelectedFile(file)}
              onPreview={() => setShowImagePreview(true)}
              disabled={!isEditable}
            />
          </Box>
        )}

        {/* Mobile File Upload Section */}
        {isMobile && (
          <>
            <AttachmentsPanel
              variant="mobile"
              selectedFile={selectedFile}
              onFileChange={(file) => setSelectedFile(file)}
              onPreview={() => setShowImagePreview(true)}
              disabled={!isEditable}
            />
            {/* Mobile-only spacer to keep the floating bar off the attachments */}
            <Box sx={{ height: 50,  mt: 1 }} />
          </>
        )}
      </Box>

      {/* Column Configuration Popover */}
      <ColumnConfigPopover
        open={Boolean(columnConfigAnchor)}
        anchorEl={columnConfigAnchor}
        onClose={() => setColumnConfigAnchor(null)}
        columns={columnDefinitions}
        columnConfig={columnConfig}
        onChange={handleColumnConfigChange}
        onShowAll={showAll}
        onShowEssentials={showEssentials}
      />

      {/* Image Preview Dialog */}
      {showImagePreview && selectedFile && (
        <Dialog
          open={showImagePreview}
          onClose={() => setShowImagePreview(false)}
          maxWidth="md"
          fullWidth
        >
          <Box sx={{ position: 'relative' }}>
            <img 
              src={URL.createObjectURL(selectedFile)} 
              alt="Preview"
              style={{ 
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
            <IconButton
              sx={(theme) => ({ 
                position: 'absolute', 
                top: 8, 
                [theme.direction === 'rtl' ? 'left' : 'right']: 8, 
                bgcolor: 'rgba(0,0,0,0.5)'
              })}
              onClick={() => setShowImagePreview(false)}
            >
              <CloseIcon sx={{ color: 'white' }} />
            </IconButton>
          </Box>
        </Dialog>
      )}

      {/* Floating Navigation Bar */}
      <FloatingNavigationBar
        key="floating-navigation-bar" // Stable key to prevent remounting
        currentBondNum={currentInvoice?.bond_num || formData.bond_num || ''}
        onNavigate={handleInvoiceNavigation}
        branchId={selectedBranchId}
      />
    </Box>
  )
}

export default InvoiceForm 